import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Calendar } from './ui/calendar';
import { toast } from 'sonner';
import { usePermissions } from '../contexts/PermissionsContext';
import AdminVacationOverview from './AdminVacationOverview';
import {
  TreePine,
  Plus,
  Calendar as CalendarIcon,
  BarChart3,
  Users,
  CheckCircle,
  Clock,
  XCircle,
  Download,
  RefreshCw,
  Filter,
  FileText,
  TrendingUp
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Vacation = () => {
  const { isTechniker, user } = usePermissions();
  const [vacations, setVacations] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [statistics, setStatistics] = useState(null);
  const [loading, setLoading] = useState(true);
  // Techniker starten direkt im "Meine Urlaube" Tab
  const [activeTab, setActiveTab] = useState(isTechniker() ? 'my-vacations' : 'overview');
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedEmployee, setSelectedEmployee] = useState('');
  const [showVacationDialog, setShowVacationDialog] = useState(false);
  
  const [vacationForm, setVacationForm] = useState({
    employee_id: '',
    vacation_type: 'jahresurlaub',
    start_date: '',
    end_date: '',
    reason: '',
    notes: ''
  });
  
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchInitialData();
  }, []);

  useEffect(() => {
    if (selectedYear) {
      fetchVacations();
      fetchStatistics();
    }
  }, [selectedYear, selectedEmployee]);

  const fetchInitialData = async () => {
    try {
      if (!isTechniker()) {
        // Nur für Admin/Manager: Load all employees
        const employeesResponse = await axios.get(`${API}/employees`);
        setEmployees(employeesResponse.data);
      } else {
        // Techniker: Setze nur eigene Employee-ID
        if (user && user.employee_id) {
          setSelectedEmployee(user.employee_id);
          setVacationForm(prev => ({...prev, employee_id: user.employee_id}));
        }
      }
    } catch (error) {
      console.error('Error fetching employees:', error);
      toast.error('Fehler beim Laden der Mitarbeiter');
    }
  };

  const fetchVacations = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        year: selectedYear.toString()
      });
      
      if (isTechniker()) {
        // Techniker: Immer nur eigene Urlaube laden
        if (user && user.employee_id) {
          params.append('employee_id', user.employee_id);
        }
      } else {
        // Admin/Manager: Filterung basierend auf selectedEmployee
        if (selectedEmployee && selectedEmployee !== 'all') {
          params.append('employee_id', selectedEmployee);
        }
      }
      
      const response = await axios.get(`${API}/vacations?${params}`);
      setVacations(response.data);
    } catch (error) {
      console.error('Error fetching vacations:', error);
      toast.error('Fehler beim Laden der Urlaubsdaten');
    } finally {
      setLoading(false);
    }
  };

  const fetchStatistics = async () => {
    try {
      if (isTechniker() && user && user.employee_id) {
        // Techniker: Nur eigene Vacation Balance laden
        const response = await axios.get(`${API}/vacation-balance/${user.employee_id}/${selectedYear}`);
        // Umwandlung der Balance-Daten in Statistics-Format für Techniker
        setStatistics({
          total_employees: 1, // Nur der Techniker selbst
          total_vacations: vacations.length,
          pending_requests: vacations.filter(v => v.status === 'wartend').length,
          approved_vacations: vacations.filter(v => v.status === 'genehmigt').length,
          rejected_requests: vacations.filter(v => v.status === 'abgelehnt').length,
          total_days_taken: response.data.used_days,
          average_days_per_employee: response.data.used_days, // Nur der Techniker
          department_breakdown: [{
            department: "Techniker",
            employee_count: 1,
            total_days: response.data.used_days,
            avg_days: response.data.used_days
          }],
          vacation_balance: response.data // Zusätzliche Balance-Informationen
        });
      } else {
        // Admin/Manager: Normale Statistics
        const response = await axios.get(`${API}/vacation-statistics/${selectedYear}`);
        setStatistics(response.data);
      }
    } catch (error) {
      console.error('Error fetching statistics:', error);
      toast.error('Fehler beim Laden der Statistiken');
    }
  };

  const handleVacationSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      await axios.post(`${API}/vacations`, vacationForm);
      toast.success('Urlaubsantrag erfolgreich erstellt');
      
      setShowVacationDialog(false);
      resetVacationForm();
      fetchVacations();
      fetchStatistics();
    } catch (error) {
      console.error('Error creating vacation:', error);
      const errorMessage = typeof error.response?.data?.detail === 'string' 
        ? error.response.data.detail 
        : 'Fehler beim Erstellen des Urlaubsantrags';
      toast.error(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdateVacationStatus = async (vacationId, status) => {
    try {
      await axios.put(`${API}/vacations/${vacationId}`, { status });
      toast.success(`Urlaubsantrag ${getStatusLabel(status).toLowerCase()}`);
      fetchVacations();
      fetchStatistics();
    } catch (error) {
      console.error('Error updating vacation status:', error);
      toast.error('Fehler beim Aktualisieren des Status');
    }
  };

  const handleDeleteVacation = async (vacationId) => {
    if (window.confirm('Sind Sie sicher, dass Sie diesen Urlaubsantrag löschen möchten?')) {
      try {
        await axios.delete(`${API}/vacations/${vacationId}`);
        toast.success('Urlaubsantrag erfolgreich gelöscht');
        fetchVacations();
        fetchStatistics();
      } catch (error) {
        console.error('Error deleting vacation:', error);
        toast.error('Fehler beim Löschen des Urlaubsantrags');
      }
    }
  };

  const resetVacationForm = () => {
    setVacationForm({
      employee_id: isTechniker() && user ? user.employee_id : '',
      vacation_type: 'jahresurlaub',
      start_date: '',
      end_date: '',
      reason: '',
      notes: ''
    });
  };

  const getStatusBadge = (status) => {
    const variants = {
      beantragt: 'bg-amber-100 text-amber-800 border-amber-300',
      genehmigt: 'bg-emerald-100 text-emerald-800 border-emerald-300',
      abgelehnt: 'bg-red-100 text-red-800 border-red-300',
      storniert: 'bg-gray-100 text-gray-800 border-gray-300'
    };
    return variants[status] || 'bg-gray-100 text-gray-800 border-gray-300';
  };

  const getStatusIcon = (status) => {
    const icons = {
      beantragt: <Clock className="w-4 h-4" />,
      genehmigt: <CheckCircle className="w-4 h-4" />,
      abgelehnt: <XCircle className="w-4 h-4" />,
      storniert: <XCircle className="w-4 h-4" />
    };
    return icons[status] || <Clock className="w-4 h-4" />;
  };

  const getStatusLabel = (status) => {
    const labels = {
      beantragt: 'Beantragt',
      genehmigt: 'Genehmigt',
      abgelehnt: 'Abgelehnt',
      storniert: 'Storniert'
    };
    return labels[status] || status;
  };

  const getVacationTypeLabel = (type) => {
    const labels = {
      jahresurlaub: 'Jahresurlaub',
      krankheitstage: 'Krankheitstage',
      sonderurlaub: 'Sonderurlaub',
      unbezahlt: 'Unbezahlt'
    };
    return labels[type] || type;
  };

  const getEmployeeName = (employeeId) => {
    const employee = employees.find(emp => emp.id === employeeId);
    return employee ? `${employee.first_name} ${employee.last_name}` : 'Unbekannt';
  };

  const isWorkday = (date) => {
    const day = date.getDay();
    return day >= 1 && day <= 5; // Monday to Friday
  };

  const years = Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - 2 + i);

  return (
    <div className="min-h-full bg-slate-50 p-6" data-testid="vacation-page">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-3">
              <TreePine className="w-8 h-8 text-emerald-600" />
              Urlaub 🏴
            </h1>
            <p className="text-slate-600 mt-1">Urlaubsverwaltung für Mitarbeiter (nur Arbeitstage Mo-Fr)</p>
          </div>
          
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => {fetchVacations(); fetchStatistics();}}
              className="gap-2"
              data-testid="refresh-vacation-button"
            >
              <RefreshCw className="w-4 h-4" />
              Aktualisieren
            </Button>
          </div>
        </div>
      </div>

      {/* Filters - Nur für Admin/Manager */}
      {!isTechniker() && (
        <Card className="modern-card mb-6">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Jahr</label>
                <Select value={selectedYear.toString()} onValueChange={(value) => setSelectedYear(parseInt(value))}>
                  <SelectTrigger className="w-32" data-testid="year-filter-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {years.map(year => (
                      <SelectItem key={year} value={year.toString()}>{year}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">Mitarbeiter</label>
                <Select value={selectedEmployee} onValueChange={setSelectedEmployee}>
                  <SelectTrigger className="w-64" data-testid="employee-filter-select">
                    <SelectValue placeholder="Alle Mitarbeiter" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Alle Mitarbeiter</SelectItem>
                    {employees.map(employee => (
                      <SelectItem key={employee.id} value={employee.id}>
                        {employee.first_name} {employee.last_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Statistics Cards - Nur für Admin/Manager */}
      {!isTechniker() && statistics && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <Card className="modern-card border-l-4 border-l-emerald-500">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-600 mb-1">Gesamte Urlaubstage</p>
                  <p className="text-3xl font-bold text-slate-900" data-testid="total-vacation-days">
                    {statistics.total_vacation_days}
                  </p>
                </div>
                <TreePine className="w-8 h-8 text-emerald-600" />
              </div>
            </CardContent>
          </Card>

          <Card className="modern-card border-l-4 border-l-amber-500">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-600 mb-1">Offene Anträge</p>
                  <p className="text-3xl font-bold text-slate-900" data-testid="pending-requests">
                    {statistics.pending_requests}
                  </p>
                </div>
                <Clock className="w-8 h-8 text-amber-600" />
              </div>
            </CardContent>
          </Card>

          <Card className="modern-card border-l-4 border-l-green-500">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-600 mb-1">Genehmigte Anträge</p>
                  <p className="text-3xl font-bold text-slate-900" data-testid="approved-requests">
                    {statistics.approved_requests}
                  </p>
                </div>
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
            </CardContent>
          </Card>

          <Card className="modern-card border-l-4 border-l-blue-500">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-600 mb-1">Mitarbeiter</p>
                  <p className="text-3xl font-bold text-slate-900" data-testid="total-employees-vacation">
                    {statistics.total_employees}
                  </p>
                </div>
                <Users className="w-8 h-8 text-blue-600" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Tabs - Angepasst für Techniker */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        {isTechniker() ? (
          // Techniker-Tabs: Nur eigene Daten
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="my-vacations" className="gap-2">
              <CalendarIcon className="w-4 h-4" />
              Meine Urlaube
            </TabsTrigger>
            <TabsTrigger value="my-statistics" className="gap-2">
              <BarChart3 className="w-4 h-4" />
              Meine Statistiken
            </TabsTrigger>
          </TabsList>
        ) : (
          // Admin/Manager-Tabs: Alle Daten
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="overview" className="gap-2">
              <CalendarIcon className="w-4 h-4" />
              Übersicht
            </TabsTrigger>
            <TabsTrigger value="requests" className="gap-2">
              <FileText className="w-4 h-4" />
              Anträge
            </TabsTrigger>
            <TabsTrigger value="admin-overview" className="gap-2">
              <Users className="w-4 h-4" />
              Mitarbeiter-Ansprüche
            </TabsTrigger>
            <TabsTrigger value="statistics" className="gap-2">
              <BarChart3 className="w-4 h-4" />
              Statistiken
            </TabsTrigger>
          </TabsList>
        )}

        {/* Overview Tab */}
        <TabsContent value="overview" className="mt-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-semibold text-slate-900">Urlaubsübersicht {selectedYear}</h2>
            <Dialog open={showVacationDialog} onOpenChange={setShowVacationDialog}>
              <DialogTrigger asChild>
                <Button 
                  className="gap-2 bg-emerald-600 hover:bg-emerald-700"
                  onClick={resetVacationForm}
                  data-testid="add-vacation-button"
                >
                  <Plus className="w-4 h-4" />
                  Urlaub beantragen
                </Button>
              </DialogTrigger>
              <VacationForm
                formData={vacationForm}
                setFormData={setVacationForm}
                onSubmit={handleVacationSubmit}
                submitting={submitting}
                employees={employees}
                onClose={() => setShowVacationDialog(false)}
              />
            </Dialog>
          </div>

          <VacationOverview 
            vacations={vacations}
            employees={employees}
            loading={loading}
            onUpdateStatus={handleUpdateVacationStatus}
            onDelete={handleDeleteVacation}
            getStatusBadge={getStatusBadge}
            getStatusIcon={getStatusIcon}
            getStatusLabel={getStatusLabel}
            getVacationTypeLabel={getVacationTypeLabel}
            getEmployeeName={getEmployeeName}
          />
        </TabsContent>

        {/* Requests Tab */}
        <TabsContent value="requests" className="mt-6">
          <VacationRequests 
            vacations={vacations}
            employees={employees}
            loading={loading}
            onUpdateStatus={handleUpdateVacationStatus}
            onDelete={handleDeleteVacation}
            getStatusBadge={getStatusBadge}
            getStatusIcon={getStatusIcon}
            getStatusLabel={getStatusLabel}
            getVacationTypeLabel={getVacationTypeLabel}
            getEmployeeName={getEmployeeName}
          />
        </TabsContent>

        {/* Admin Overview Tab */}
        <TabsContent value="admin-overview" className="mt-6">
          <AdminVacationOverview 
            selectedYear={selectedYear}
            onYearChange={setSelectedYear}
          />
        </TabsContent>

        {/* Statistics Tab */}
        <TabsContent value="statistics" className="mt-6">
          <VacationStatistics 
            statistics={statistics}
            selectedYear={selectedYear}
          />
        </TabsContent>

        {/* Techniker-spezifische Tabs */}
        <TabsContent value="my-vacations" className="mt-6">
          <div className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
            <div className="flex items-center gap-2 mb-2">
              <TreePine className="w-5 h-5 text-blue-600" />
              <span className="font-medium text-blue-900">Meine Urlaubsübersicht</span>
            </div>
            <p className="text-blue-700 text-sm">
              Hier sehen Sie nur Ihre eigenen Urlaubsanträge und können neue Anträge stellen.
            </p>
          </div>

          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-semibold text-slate-900">Meine Urlaube {selectedYear}</h2>
            <Dialog open={showVacationDialog} onOpenChange={setShowVacationDialog}>
              <DialogTrigger asChild>
                <Button 
                  className="gap-2 bg-emerald-600 hover:bg-emerald-700"
                  onClick={resetVacationForm}
                >
                  <Plus className="w-4 h-4" />
                  Urlaub beantragen
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-[500px]">
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2">
                    <TreePine className="w-5 h-5 text-emerald-600" />
                    Neuen Urlaubsantrag stellen
                  </DialogTitle>
                </DialogHeader>
                <VacationForm 
                  formData={vacationForm}
                  setFormData={setVacationForm}
                  employees={employees}
                  onSubmit={handleVacationSubmit}
                  submitting={submitting}
                  isTechniker={true}
                  onClose={() => setShowVacationDialog(false)}
                />
              </DialogContent>
            </Dialog>
          </div>

          <VacationOverview 
            vacations={vacations}
            employees={employees}
            loading={loading}
            onUpdateStatus={handleUpdateVacationStatus}
            onDelete={handleDeleteVacation}
            getStatusBadge={getStatusBadge}
            getStatusIcon={getStatusIcon}
            getStatusLabel={getStatusLabel}
            getVacationTypeLabel={getVacationTypeLabel}
            getEmployeeName={getEmployeeName}
            isTechniker={true}
          />
        </TabsContent>

        <TabsContent value="my-statistics" className="mt-6">
          <div className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
            <div className="flex items-center gap-2 mb-2">
              <BarChart3 className="w-5 h-5 text-blue-600" />
              <span className="font-medium text-blue-900">Meine Urlaubsstatistiken</span>
            </div>
            <p className="text-blue-700 text-sm">
              Übersicht über Ihre Urlaubstage: verfügbar, beantragt, genehmigt und verbraucht.
            </p>
          </div>

          <TechnikerVacationStatistics 
            statistics={statistics}
            selectedYear={selectedYear}
            user={user}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
};

// Vacation Overview Component
const VacationOverview = ({ 
  vacations, employees, loading, onUpdateStatus, onDelete,
  getStatusBadge, getStatusIcon, getStatusLabel, getVacationTypeLabel, getEmployeeName,
  isTechniker = false
}) => {
  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600"></div>
        <span className="ml-3 text-slate-600">Lade Urlaubsdaten...</span>
      </div>
    );
  }

  if (vacations.length === 0) {
    return (
      <Card className="modern-card text-center py-12">
        <CardContent>
          <TreePine className="w-12 h-12 text-slate-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-slate-900 mb-2">Keine Urlaubsanträge gefunden</h3>
          <p className="text-slate-600">Erstellen Sie den ersten Urlaubsantrag.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="grid grid-cols-1 gap-4">
      {vacations.map((vacation) => (
        <VacationCard
          key={vacation.id}
          vacation={vacation}
          onUpdateStatus={onUpdateStatus}
          onDelete={onDelete}
          getStatusBadge={getStatusBadge}
          getStatusIcon={getStatusIcon}
          getStatusLabel={getStatusLabel}
          getVacationTypeLabel={getVacationTypeLabel}
          getEmployeeName={getEmployeeName}
          isTechniker={isTechniker}
        />
      ))}
    </div>
  );
};

// Vacation Card Component
const VacationCard = ({ 
  vacation, onUpdateStatus, onDelete,
  getStatusBadge, getStatusIcon, getStatusLabel, getVacationTypeLabel, getEmployeeName,
  isTechniker = false
}) => {
  return (
    <Card className="modern-card">
      <CardContent className="p-6">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-3">
              <h3 className="font-semibold text-lg">{getEmployeeName(vacation.employee_id)}</h3>
              <Badge className={`${getStatusBadge(vacation.status)} border gap-1`}>
                {getStatusIcon(vacation.status)}
                {getStatusLabel(vacation.status)}
              </Badge>
              <Badge variant="outline">
                {getVacationTypeLabel(vacation.vacation_type)}
              </Badge>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-3">
              <div>
                <p className="text-sm text-slate-600">Zeitraum:</p>
                <p className="font-medium">
                  {new Date(vacation.start_date).toLocaleDateString('de-DE')} - {' '}
                  {new Date(vacation.end_date).toLocaleDateString('de-DE')}
                </p>
              </div>
              <div>
                <p className="text-sm text-slate-600">Dauer:</p>
                <p className="font-medium">{vacation.days_count} Arbeitstage</p>
              </div>
              <div>
                <p className="text-sm text-slate-600">Erstellt:</p>
                <p className="font-medium">{new Date(vacation.created_at).toLocaleDateString('de-DE')}</p>
              </div>
            </div>
            
            {vacation.reason && (
              <div className="mb-3">
                <p className="text-sm text-slate-600">Grund:</p>
                <p className="text-sm">{vacation.reason}</p>
              </div>
            )}
            
            {vacation.notes && (
              <div className="text-sm text-slate-600 bg-slate-50 p-2 rounded">
                <strong>Notizen:</strong> {vacation.notes}
              </div>
            )}
          </div>
          
          {/* Admin-Aktionen nur für Admin/Manager */}
          {!isTechniker && (
            <div className="flex gap-2 ml-4">
              {vacation.status === 'beantragt' && (
                <>
                  <Button
                    size="sm"
                    onClick={() => onUpdateStatus(vacation.id, 'genehmigt')}
                    className="bg-emerald-600 hover:bg-emerald-700"
                    data-testid={`approve-vacation-${vacation.id}`}
                  >
                    <CheckCircle className="w-4 h-4 mr-1" />
                    Genehmigen
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => onUpdateStatus(vacation.id, 'abgelehnt')}
                    className="text-red-600 border-red-600 hover:bg-red-50"
                    data-testid={`reject-vacation-${vacation.id}`}
                  >
                    <XCircle className="w-4 h-4 mr-1" />
                    Ablehnen
                  </Button>
                </>
              )}
              <Button
                size="sm"
                variant="ghost"
                onClick={() => onDelete(vacation.id)}
                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                data-testid={`delete-vacation-${vacation.id}`}
              >
                Löschen
              </Button>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

// Vacation Requests Component
const VacationRequests = ({ 
  vacations, employees, loading, onUpdateStatus, onDelete,
  getStatusBadge, getStatusIcon, getStatusLabel, getVacationTypeLabel, getEmployeeName 
}) => {
  const pendingVacations = vacations.filter(v => v.status === 'beantragt');
  
  return (
    <div>
      <h2 className="text-xl font-semibold text-slate-900 mb-6">Offene Urlaubsanträge</h2>
      
      {pendingVacations.length === 0 ? (
        <Card className="modern-card text-center py-12">
          <CardContent>
            <Clock className="w-12 h-12 text-slate-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-slate-900 mb-2">Keine offenen Anträge</h3>
            <p className="text-slate-600">Alle Urlaubsanträge sind bearbeitet.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 gap-4">
          {pendingVacations.map((vacation) => (
            <VacationCard
              key={vacation.id}
              vacation={vacation}
              onUpdateStatus={onUpdateStatus}
              onDelete={onDelete}
              getStatusBadge={getStatusBadge}
              getStatusIcon={getStatusIcon}
              getStatusLabel={getStatusLabel}
              getVacationTypeLabel={getVacationTypeLabel}
              getEmployeeName={getEmployeeName}
            />
          ))}
        </div>
      )}
    </div>
  );
};

// Vacation Statistics Component  
const VacationStatistics = ({ statistics, selectedYear }) => {
  if (!statistics) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600"></div>
        <span className="ml-3 text-slate-600">Lade Statistiken...</span>
      </div>
    );
  }

  const months = [
    'Januar', 'Februar', 'März', 'April', 'Mai', 'Juni',
    'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'
  ];

  return (
    <div>
      <h2 className="text-xl font-semibold text-slate-900 mb-6">Urlaubsstatistiken {selectedYear}</h2>
      
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="modern-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-emerald-600" />
              Monatsübersicht
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {months.map((month, index) => {
                const monthData = statistics.monthly_breakdown[index + 1] || { requests: 0, total_days: 0 };
                return (
                  <div key={index} className="flex items-center justify-between p-3 bg-slate-50 rounded">
                    <span className="font-medium">{month}</span>
                    <div className="text-right">
                      <div className="text-sm font-medium">{monthData.requests} Anträge</div>
                      <div className="text-xs text-slate-600">{monthData.total_days} Tage</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
        
        <Card className="modern-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-emerald-600" />
              Jahresübersicht
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex justify-between items-center p-3 bg-emerald-50 rounded border border-emerald-200">
                <span className="font-medium">Gesamte Urlaubstage</span>
                <span className="text-2xl font-bold text-emerald-800">{statistics.total_vacation_days}</span>
              </div>
              
              <div className="flex justify-between items-center p-3 bg-amber-50 rounded border border-amber-200">
                <span className="font-medium">Offene Anträge</span>
                <span className="text-2xl font-bold text-amber-800">{statistics.pending_requests}</span>
              </div>
              
              <div className="flex justify-between items-center p-3 bg-green-50 rounded border border-green-200">
                <span className="font-medium">Genehmigte Anträge</span>
                <span className="text-2xl font-bold text-green-800">{statistics.approved_requests}</span>
              </div>
              
              <div className="flex justify-between items-center p-3 bg-blue-50 rounded border border-blue-200">
                <span className="font-medium">Durchschnitt pro MA</span>
                <span className="text-2xl font-bold text-blue-800">
                  {statistics.total_employees > 0 ? Math.round(statistics.total_vacation_days / statistics.total_employees) : 0}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// Vacation Form Component
const VacationForm = ({ formData, setFormData, onSubmit, submitting, employees, onClose, isTechniker = false }) => {
  const handleDateChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
  };

  return (
    <DialogContent className="sm:max-w-md" aria-describedby="vacation-form-description">
      <DialogHeader>
        <DialogTitle>
          {isTechniker ? 'Meinen Urlaub beantragen' : 'Urlaubsantrag erstellen'}
        </DialogTitle>
        <p id="vacation-form-description" className="text-sm text-slate-600">
          {isTechniker 
            ? 'Erstellen Sie einen neuen Urlaubsantrag für sich selbst.'
            : 'Erstellen Sie einen neuen Urlaubsantrag für einen Mitarbeiter.'
          }
        </p>
      </DialogHeader>
      
      <form onSubmit={onSubmit} className="space-y-4">
        {/* Mitarbeiter-Auswahl nur für Admin/Manager */}
        {!isTechniker && (
          <div>
            <label className="block text-sm font-medium mb-1">Mitarbeiter</label>
            <Select value={formData.employee_id} onValueChange={(value) => setFormData({ ...formData, employee_id: value })}>
              <SelectTrigger data-testid="vacation-employee-select">
                <SelectValue placeholder="Mitarbeiter auswählen..." />
              </SelectTrigger>
              <SelectContent>
                {employees.filter(emp => emp.status === 'aktiv').map(employee => (
                  <SelectItem key={employee.id} value={employee.id}>
                    {employee.first_name} {employee.last_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        <div>
          <label className="block text-sm font-medium mb-1">Urlaubsart</label>
          <Select value={formData.vacation_type} onValueChange={(value) => setFormData({ ...formData, vacation_type: value })}>
            <SelectTrigger data-testid="vacation-type-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="jahresurlaub">Jahresurlaub</SelectItem>
              <SelectItem value="krankheitstage">Krankheitstage</SelectItem>
              <SelectItem value="sonderurlaub">Sonderurlaub</SelectItem>
              <SelectItem value="unbezahlt">Unbezahlt</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-1">Startdatum</label>
            <Input
              type="date"
              value={formData.start_date}
              onChange={(e) => handleDateChange('start_date', e.target.value)}
              required
              data-testid="vacation-start-date"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Enddatum</label>
            <Input
              type="date"
              value={formData.end_date}
              onChange={(e) => handleDateChange('end_date', e.target.value)}
              required
              data-testid="vacation-end-date"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">Grund (optional)</label>
          <Input
            value={formData.reason || ''}
            onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
            placeholder="Grund für den Urlaub..."
            data-testid="vacation-reason"
          />
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">Notizen (optional)</label>
          <Input
            value={formData.notes || ''}
            onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
            placeholder="Zusätzliche Informationen..."
            data-testid="vacation-notes"
          />
        </div>

        <div className="text-xs text-slate-500 bg-blue-50 p-3 rounded border border-blue-200">
          <strong>Hinweis:</strong> Es werden nur Arbeitstage (Montag-Freitag) gezählt. 
          Deutsche Feiertage werden automatisch ausgeschlossen.
        </div>

        <div className="flex justify-end gap-2 pt-4">
          <Button 
            type="button" 
            variant="outline" 
            disabled={submitting}
            onClick={onClose}
          >
            Abbrechen
          </Button>
          <Button type="submit" disabled={submitting} data-testid="save-vacation-button">
            {submitting ? (
              <>
                <div className="loading-spinner mr-2"></div>
                Erstellen...
              </>
            ) : (
              'Antrag erstellen'
            )}
          </Button>
        </div>
      </form>
    </DialogContent>
  );
};

// Techniker Vacation Statistics Component
const TechnikerVacationStatistics = ({ statistics, selectedYear, user }) => {
  if (!statistics || !statistics.vacation_balance) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600"></div>
        <span className="ml-3 text-slate-600">Lade Ihre Urlaubsdaten...</span>
      </div>
    );
  }

  const balance = statistics.vacation_balance;

  return (
    <div className="space-y-6">
      {/* Persönliche Urlaubsbilanz */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-600 mb-1">Jahresanspruch</p>
                <p className="text-2xl font-bold text-slate-900">{balance.annual_entitlement}</p>
                <p className="text-xs text-slate-500">Tage {selectedYear}</p>
              </div>
              <CalendarIcon className="w-8 h-8 text-blue-600" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-600 mb-1">Verbrauchte Tage</p>
                <p className="text-2xl font-bold text-red-600">{balance.used_days}</p>
                <p className="text-xs text-slate-500">Genehmigt</p>
              </div>
              <CheckCircle className="w-8 h-8 text-red-600" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-600 mb-1">Beantragte Tage</p>
                <p className="text-2xl font-bold text-yellow-600">{balance.pending_days}</p>
                <p className="text-xs text-slate-500">In Bearbeitung</p>
              </div>
              <Clock className="w-8 h-8 text-yellow-600" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-600 mb-1">Verbleibende Tage</p>
                <p className="text-2xl font-bold text-emerald-600">{balance.remaining_days}</p>
                <p className="text-xs text-slate-500">Verfügbar</p>
              </div>
              <TreePine className="w-8 h-8 text-emerald-600" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Urlaubsstatus-Übersicht */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BarChart3 className="w-5 h-5" />
            Meine Urlaubsübersicht {selectedYear}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 bg-green-50 rounded-lg">
              <div className="flex items-center gap-3">
                <CheckCircle className="w-5 h-5 text-green-600" />
                <span className="font-medium text-green-900">Genehmigte Anträge</span>
              </div>
              <Badge className="bg-green-600">{statistics.approved_vacations}</Badge>
            </div>

            <div className="flex items-center justify-between p-4 bg-yellow-50 rounded-lg">
              <div className="flex items-center gap-3">
                <Clock className="w-5 h-5 text-yellow-600" />
                <span className="font-medium text-yellow-900">Wartende Anträge</span>
              </div>
              <Badge className="bg-yellow-600">{statistics.pending_requests}</Badge>
            </div>

            <div className="flex items-center justify-between p-4 bg-red-50 rounded-lg">
              <div className="flex items-center gap-3">
                <XCircle className="w-5 h-5 text-red-600" />
                <span className="font-medium text-red-900">Abgelehnte Anträge</span>
              </div>
              <Badge className="bg-red-600">{statistics.rejected_requests}</Badge>
            </div>

            <div className="flex items-center justify-between p-4 bg-blue-50 rounded-lg">
              <div className="flex items-center gap-3">
                <FileText className="w-5 h-5 text-blue-600" />
                <span className="font-medium text-blue-900">Gesamte Anträge</span>
              </div>
              <Badge className="bg-blue-600">{statistics.total_vacations}</Badge>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Urlaubsprogress */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="w-5 h-5" />
            Urlaubsfortschritt {selectedYear}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <div className="flex justify-between text-sm">
              <span>Verbrauchte Tage</span>
              <span>{balance.used_days} von {balance.annual_entitlement} Tagen</span>
            </div>
            <div className="w-full bg-slate-200 rounded-full h-3">
              <div 
                className="bg-emerald-600 h-3 rounded-full transition-all duration-300" 
                style={{ width: `${(balance.used_days / balance.annual_entitlement) * 100}%` }}
              ></div>
            </div>
            <p className="text-xs text-slate-600">
              Sie haben {((balance.used_days / balance.annual_entitlement) * 100).toFixed(1)}% 
              Ihres Jahresurlaubs verbraucht
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Vacation;